// src/pages/HousekeepingPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Input, Modal, Select, Space, Spin, Switch, Table } from "antd";
import { useAuth } from "../context/AuthContext";
import housekeepingApi from "../api/housekeepingApi";
import HousekeepingModal from "./HousekeepingModal";
import {
  CardTitle,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatusTag,
  TabBar,
  fmtDateTime,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconInfo, IconPlus } from "../components/ui/icons";
import notify from "../utils/notify";

const TASK_STATUS = {
  PENDING: "Pending",
  IN_PROGRESS: "InProgress",
  COMPLETED: "Completed",
};

const STATUS_META = {
  [TASK_STATUS.PENDING]: { label: "Pending" },
  [TASK_STATUS.IN_PROGRESS]: { label: "In progress" },
  [TASK_STATUS.COMPLETED]: { label: "Completed" },
};

/* ------------------------
   Safe property readers (camelCase/PascalCase)
------------------------ */
const pick = (o, keys) => keys.find((k) => o && Object.prototype.hasOwnProperty.call(o, k)) ?? null;
const read = (o, keys) => (o && keys.some((k) => k in o) ? o[pick(o, keys)] : undefined);

const taskIdOf = (t) => read(t, ["housekeepingTaskId", "HousekeepingTaskId", "id", "Id"]);
const itemIdOf = (it) => read(it, ["checklistItemId", "ChecklistItemId", "id", "Id"]) ?? 0;
const itemDescOf = (it) => read(it, ["description", "Description"]) ?? "";
const itemDoneOf = (it) => !!read(it, ["isCompleted", "IsCompleted"]);
const itemDoneAtOf = (it) => read(it, ["completedAt", "CompletedAt"]);

const normalizeChecklistItem = (it) => ({
  checklistItemId: itemIdOf(it),
  description: itemDescOf(it),
  isCompleted: itemDoneOf(it),
  completedAt: itemDoneAtOf(it) ?? null,
  // keep any temp keys if present
  _tempId: it?._tempId,
});

const HousekeepingPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;
  const userId = authData?.userId;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedTask, setSelectedTask] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [searchValue, setSearchValue] = useState("");

  const [showInfo, setShowInfo] = useState(false);

  // checklist modal
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistTask, setChecklistTask] = useState(null);
  const [newChecklistText, setNewChecklistText] = useState("");

  /* ------------------------
     AntD notifications (v5/v6)
  ------------------------ */
  const notifySuccess = (message, description) => notify.success({ message, description });
  const notifyError = (message, description) => notify.error({ message, description });
  const notifyWarning = (message, description) => notify.warning({ message, description });

  /* ------------------------
     API helpers
  ------------------------ */
  const apiGetMyTasks = async () => housekeepingApi.getMyTasks(hotelId);
  const apiUpdateTask = async (taskId, dto) => housekeepingApi.updateTask(taskId, hotelId, dto);
  const apiGetTaskById = async (taskId) => housekeepingApi.getTaskById(taskId, hotelId);
  const apiGetChecklistItems = async (taskId) => housekeepingApi.getChecklistItems(taskId);
  const apiUpdateChecklist = async (taskId, itemsDtoArray) => housekeepingApi.updateChecklist(taskId, itemsDtoArray);

  /* ------------------------
     Fetch tasks
  ------------------------ */
  const fetchHousekeepingTasks = async () => {
    if (!hotelId) {
      setError("Hotel context missing. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiGetMyTasks();
      const data = response?.data;

      // API sometimes returns { message: "..."} instead of []
      const list = Array.isArray(data) ? data : [];

      const formatted = list.map((task) => ({
        housekeepingTaskId: taskIdOf(task),
        title: read(task, ["title", "Title"]) ?? "",
        description: read(task, ["description", "Description"]) ?? "",
        roomId: read(task, ["roomId", "RoomId"]),
        roomNumber: read(task, ["roomNumber", "RoomNumber"]) || read(task, ["room", "Room"])?.roomNumber || "N/A",
        status: read(task, ["status", "Status"]) || TASK_STATUS.PENDING,
        priority: read(task, ["priority", "Priority"]) || "Normal",
        hotelId: read(task, ["hotelId", "HotelId"]) || hotelId,
        assignedAt: read(task, ["assignedAt", "AssignedAt"]) || read(task, ["createdDate", "CreatedDate"]) || null,
        notes: read(task, ["notes", "Notes", "assignmentNotes", "AssignmentNotes"]) || "",
      }));

      setTasks(formatted);
    } catch (err) {
      console.error("Failed to fetch housekeeping tasks:", err);
      const msg = err?.response?.data?.message || err?.message || "Failed to load housekeeping tasks";
      setError(msg);
      notifyError("Error", msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHousekeepingTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, userId, refreshKey]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => setRefreshKey((k) => k + 1), 60000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const handleRefresh = () => setRefreshKey((prev) => prev + 1);

  /* ------------------------
     Edit modal
  ------------------------ */
  const handleEditTask = (task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  const handleTaskUpdate = async (updatedTask) => {
    try {
      const updateDto = {
        status: updatedTask.status,
        notes: updatedTask.notes || null,
        completedAt: updatedTask.status === TASK_STATUS.COMPLETED ? new Date().toISOString() : null,
      };

      await apiUpdateTask(updatedTask.housekeepingTaskId, updateDto);

      setTasks((prev) =>
        prev.map((t) => (t.housekeepingTaskId === updatedTask.housekeepingTaskId ? { ...t, ...updatedTask } : t))
      );

      notifySuccess("Success", "Task updated successfully");
      closeEditModal();
    } catch (err) {
      console.error("Update failed:", err?.response?.data || err);
      notifyError("Error", err?.response?.data?.message || "Failed to update task");
    }
  };

  /* ------------------------
     Checklist: load, refresh, save (bulk PUT)
  ------------------------ */
  const openChecklist = async (task) => {
    const taskId = task?.housekeepingTaskId;
    if (!taskId) return;

    setChecklistOpen(true);
    setChecklistLoading(true);

    setChecklistTask({
      ...task,
      housekeepingTaskId: taskId,
      checklistItems: [],
    });
    setNewChecklistText("");

    try {
      const [taskRes, itemsRes] = await Promise.allSettled([apiGetTaskById(taskId), apiGetChecklistItems(taskId)]);

      const rawTask = taskRes.status === "fulfilled" ? taskRes.value?.data : null;

      let items = [];
      if (itemsRes.status === "fulfilled") {
        items = Array.isArray(itemsRes.value?.data) ? itemsRes.value.data : [];
      } else {
        // GET checklist returns 404 if empty
        const code = itemsRes.reason?.response?.status;
        if (code !== 404) throw itemsRes.reason;
      }

      setChecklistTask((prev) => ({
        ...prev,
        ...(rawTask || {}),
        housekeepingTaskId: taskIdOf(rawTask) ?? taskId,
        title: read(rawTask, ["title", "Title"]) ?? prev?.title ?? "",
        roomNumber: read(rawTask, ["roomNumber", "RoomNumber"]) ?? prev?.roomNumber ?? "N/A",
        status: read(rawTask, ["status", "Status"]) ?? prev?.status ?? TASK_STATUS.PENDING,
        notes: read(rawTask, ["notes", "Notes"]) ?? prev?.notes ?? "",
        checklistItems: items.map(normalizeChecklistItem),
      }));
    } catch (e) {
      console.error("Failed to load checklist:", e);
      notifyError("Failed to load checklist", e?.response?.data?.message || "Please try again.");
    } finally {
      setChecklistLoading(false);
    }
  };

  const refreshChecklist = async () => {
    const taskId = checklistTask?.housekeepingTaskId;
    if (!taskId) return;

    setChecklistLoading(true);
    try {
      const [taskRes, itemsRes] = await Promise.allSettled([apiGetTaskById(taskId), apiGetChecklistItems(taskId)]);

      const rawTask = taskRes.status === "fulfilled" ? taskRes.value?.data : null;

      let items = [];
      if (itemsRes.status === "fulfilled") {
        items = Array.isArray(itemsRes.value?.data) ? itemsRes.value.data : [];
      } else {
        const code = itemsRes.reason?.response?.status;
        if (code !== 404) throw itemsRes.reason;
      }

      setChecklistTask((prev) => ({
        ...prev,
        ...(rawTask || {}),
        status: read(rawTask, ["status", "Status"]) ?? prev?.status,
        notes: read(rawTask, ["notes", "Notes"]) ?? prev?.notes,
        checklistItems: items.map(normalizeChecklistItem),
      }));
    } catch (e) {
      notifyError("Failed to refresh checklist", e?.response?.data?.message || "Please try again.");
    } finally {
      setChecklistLoading(false);
    }
  };

  const saveChecklistItems = async (nextItems) => {
    const taskId = checklistTask?.housekeepingTaskId;
    if (!taskId) return;

    // DTO expected by backend: List<ChecklistItemDto>
    // NOTE: do NOT send completedAt. Server should control CompletedAt.
    const dto = (nextItems || [])
      .map((x) => ({
        checklistItemId: itemIdOf(x), // 0 for new items
        description: itemDescOf(x),
        isCompleted: itemDoneOf(x),
      }))
      .filter((x) => (x.description || "").trim().length > 0);

    setChecklistLoading(true);
    try {
      const res = await apiUpdateChecklist(taskId, dto);

      // If your updated backend returns the updated checklist + status, use it immediately
      const payload = res?.data;
      const returnedChecklist = Array.isArray(payload?.checklist) ? payload.checklist : null;

      if (returnedChecklist) {
        setChecklistTask((prev) => ({
          ...prev,
          status: payload?.taskStatus ?? prev?.status,
          checklistItems: returnedChecklist.map(normalizeChecklistItem),
        }));
      } else {
        // Fallback if backend returns only { message: "..." }
        await refreshChecklist();
      }

      await fetchHousekeepingTasks();
    } catch (e) {
      console.error("Failed to save checklist:", e);
      notifyError("Failed to update checklist", e?.response?.data?.message || "Please try again.");
      await refreshChecklist();
    } finally {
      setChecklistLoading(false);
    }
  };

  const toggleChecklistItem = async (item, checked) => {
    const id = itemIdOf(item);

    // Optimistic UI: show a timestamp immediately even before server response
    const next = (checklistTask?.checklistItems || []).map((x) => {
      const same =
        itemIdOf(x) === id ||
        (id === 0 && x._tempId && item._tempId && x._tempId === item._tempId);

      if (!same) return x;

      return {
        ...x,
        isCompleted: checked,
        completedAt: checked ? new Date().toISOString() : null,
      };
    });

    setChecklistTask((prev) => ({ ...prev, checklistItems: next }));
    await saveChecklistItems(next);
  };

  const addChecklistItem = async () => {
    const text = newChecklistText.trim();
    if (!text) return;

    const tempId = `tmp-${Date.now()}-${Math.random()}`;
    const next = [
      ...(checklistTask?.checklistItems || []),
      { checklistItemId: 0, description: text, isCompleted: false, completedAt: null, _tempId: tempId },
    ];

    setNewChecklistText("");
    setChecklistTask((prev) => ({ ...prev, checklistItems: next }));
    await saveChecklistItems(next);
    notifySuccess("Checklist updated", "Checklist item added");
  };

  const deleteChecklistItem = async (item) => {
    const id = itemIdOf(item);

    const next = (checklistTask?.checklistItems || []).filter((x) => {
      // for unsaved temp items, match by _tempId
      if (id === 0) return x._tempId !== item._tempId;
      return itemIdOf(x) !== id;
    });

    setChecklistTask((prev) => ({ ...prev, checklistItems: next }));
    await saveChecklistItems(next);
    notifySuccess("Checklist updated", "Checklist item deleted");
  };

  const checklistProgress = useMemo(() => {
    const items = checklistTask?.checklistItems || [];
    const total = items.length;
    const done = items.filter((x) => itemDoneOf(x)).length;
    return { total, done, allDone: total > 0 && done === total };
  }, [checklistTask]);

  const markTaskCompletedFromChecklist = async () => {
    if (!checklistProgress.allDone) {
      notifyWarning("Checklist not complete", "Complete all checklist items before marking the task as Completed.");
      return;
    }

    // Backend auto-completes task when all checklist items are done
    await refreshChecklist();
    await fetchHousekeepingTasks();
    setChecklistOpen(false);
    notifySuccess("Success", "Task completed (auto-completed by checklist).");
  };

  /* ------------------------
     Filtering + counts
  ------------------------ */
  const filteredTasks = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return tasks.filter((t) => {
      const statusOk = statusFilter === "All" || t.status === statusFilter;
      const priorityOk = priorityFilter === "All" || t.priority === priorityFilter;
      const text = `${t.title} ${t.description} ${t.roomNumber}`.toLowerCase();
      const searchOk = q === "" || text.includes(q);
      return statusOk && priorityOk && searchOk;
    });
  }, [tasks, statusFilter, priorityFilter, searchValue]);

  const counts = useMemo(() => {
    const c = { pending: 0, inProgress: 0, completed: 0 };
    for (const t of tasks) {
      if (t.status === TASK_STATUS.PENDING) c.pending++;
      else if (t.status === TASK_STATUS.IN_PROGRESS) c.inProgress++;
      else if (t.status === TASK_STATUS.COMPLETED) c.completed++;
    }
    return c;
  }, [tasks]);

  const renderStatus = (status) => {
    const meta = STATUS_META[status] || { label: status || "Unknown" };
    return <StatusTag status={status}>{meta.label}</StatusTag>;
  };

  const tabs = [
    { key: "All", label: "All", count: tasks.length },
    { key: TASK_STATUS.PENDING, label: "Pending", count: counts.pending },
    { key: TASK_STATUS.IN_PROGRESS, label: "In progress", count: counts.inProgress },
    { key: TASK_STATUS.COMPLETED, label: "Completed", count: counts.completed },
  ];

  /* ------------------------
     Main table columns
  ------------------------ */
  const columns = [
    {
      title: "Task",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{text || "Untitled task"}</span>
          <span className="hms-cell-sub">Room {record.roomNumber || "—"}</span>
        </div>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 130,
      render: (p) => <StatusTag status={p}>{p}</StatusTag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus,
      width: 150,
    },
    {
      title: "Assigned",
      dataIndex: "assignedAt",
      key: "assignedAt",
      render: (date) => (date ? fmtDateTime(date) : <span className="hms-muted">—</span>),
      sorter: (a, b) => new Date(a.assignedAt || 0) - new Date(b.assignedAt || 0),
      width: 200,
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      align: "right",
      width: 110,
      render: (_, record) => (
        <span onClick={(e) => e.stopPropagation()}>
          <RowActions
            actions={[
              { key: "edit", label: "Edit task", icon: "edit", onClick: () => handleEditTask(record) },
              { key: "checklist", label: "Checklist", icon: "checklist", onClick: () => openChecklist(record) },
            ]}
          />
        </span>
      ),
    },
  ];

  const taskLocked = checklistTask?.status === TASK_STATUS.COMPLETED;

  return (
    <div className="hms-page hk-page">
      <PageHeader
        actions={
          <>
            <label className="hms-switch-label">
              <Switch checked={autoRefresh} onChange={setAutoRefresh} />
              Auto refresh
            </label>
            <Button icon={<IconInfo />} onClick={() => setShowInfo(true)}>
              Guide
            </Button>
          </>
        }
      />

      {error && (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          showIcon
          message="Could not load tasks"
          description={error}
          action={<Button onClick={handleRefresh}>Retry</Button>}
        />
      )}

      <SectionCard
        flush
        title={<CardTitle count={tasks.length}>My housekeeping tasks</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchValue, onChange: setSearchValue, placeholder: "Search title or room" }}
            onRefresh={handleRefresh}
            loading={loading}
          >
            <Select
              style={{ width: 150 }}
              value={priorityFilter}
              onChange={setPriorityFilter}
              options={[
                { label: "All priorities", value: "All" },
                { label: "Low", value: "Low" },
                { label: "Normal", value: "Normal" },
                { label: "High", value: "High" },
                { label: "Urgent", value: "Urgent" },
              ]}
            />
          </ListToolbar>
        }
      >
        <TabBar tabs={tabs} value={statusFilter} onChange={setStatusFilter} />

        <Table
          className="hms-table"
          columns={columns}
          dataSource={filteredTasks}
          rowKey="housekeepingTaskId"
          loading={loading}
          pagination={tablePagination("task")}
          scroll={{ x: 800 }}
          locale={{ emptyText: <div className="hms-empty">No tasks match your filters</div> }}
        />
      </SectionCard>

      {isModalOpen && (
        <HousekeepingModal
          open={isModalOpen}
          task={selectedTask}
          onClose={closeEditModal}
          onSave={handleTaskUpdate}
          statusOptions={Object.values(TASK_STATUS)}
          statusLabels={{
            [TASK_STATUS.PENDING]: STATUS_META[TASK_STATUS.PENDING].label,
            [TASK_STATUS.IN_PROGRESS]: STATUS_META[TASK_STATUS.IN_PROGRESS].label,
            [TASK_STATUS.COMPLETED]: STATUS_META[TASK_STATUS.COMPLETED].label,
          }}
        />
      )}

      <Modal
        title={`Checklist${checklistTask?.housekeepingTaskId ? ` for task #${checklistTask.housekeepingTaskId}` : ""}`}
        open={checklistOpen}
        onCancel={() => setChecklistOpen(false)}
        footer={
          <div className="hms-form-actions" style={{ marginTop: 0 }}>
            <Button onClick={() => setChecklistOpen(false)}>Close</Button>
            <Button
              type="primary"
              loading={checklistLoading}
              disabled={!checklistProgress.allDone || taskLocked}
              onClick={markTaskCompletedFromChecklist}
            >
              Mark task completed
            </Button>
          </div>
        }
        width={640}
        destroyOnHidden
      >
        {!checklistTask ? (
          checklistLoading ? (
            <div style={{ padding: 36, textAlign: "center" }}>
              <Spin />
            </div>
          ) : (
            <div className="hms-empty">No task loaded</div>
          )
        ) : (
          <>
            <div className="hms-summary">
              <div>
                <div className="hms-summary-title">{checklistTask.title || "Untitled task"}</div>
                <div className="hms-summary-sub">
                  Room {checklistTask.roomNumber || "—"} · {checklistProgress.done} of {checklistProgress.total} done
                </div>
              </div>
              {renderStatus(checklistTask.status)}
            </div>

            <div className="hms-field">
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  value={newChecklistText}
                  onChange={(e) => setNewChecklistText(e.target.value)}
                  placeholder="Add an item, e.g. Replace towels"
                  onPressEnter={addChecklistItem}
                  disabled={checklistLoading || taskLocked}
                />
                <Button
                  type="primary"
                  icon={<IconPlus />}
                  onClick={addChecklistItem}
                  loading={checklistLoading}
                  disabled={!newChecklistText.trim() || taskLocked}
                >
                  Add
                </Button>
              </Space.Compact>
            </div>

            {(checklistTask.checklistItems || []).length === 0 ? (
              <div className="hms-empty">No checklist items yet</div>
            ) : (
              <ul className="hms-checklist">
                {(checklistTask.checklistItems || []).map((it) => {
                  const done = itemDoneOf(it);
                  const doneAt = itemDoneAtOf(it);
                  return (
                    <li key={String(itemIdOf(it) || it._tempId)}>
                      <Checkbox checked={done} disabled={checklistLoading || taskLocked} onChange={(e) => toggleChecklistItem(it, e.target.checked)}>
                        <span className={done ? "is-done" : ""}>{itemDescOf(it) || `Item #${itemIdOf(it)}`}</span>
                      </Checkbox>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {done && <span className="hms-cell-sub">{doneAt ? fmtDateTime(doneAt) : "Completed"}</span>}
                        {!taskLocked && (
                          <RowActions
                            actions={[
                              {
                                key: "delete",
                                label: "Delete item",
                                icon: "delete",
                                danger: true,
                                confirm: "Delete this checklist item?",
                                okText: "Delete",
                                onClick: () => deleteChecklistItem(it),
                              },
                            ]}
                          />
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            {!checklistProgress.allDone && checklistProgress.total > 0 && !taskLocked && (
              <p className="hms-modal-note" style={{ marginTop: 12 }}>
                Complete every item to enable "Mark task completed".
              </p>
            )}
          </>
        )}
      </Modal>

      <Modal
        title="How to use Housekeeping"
        open={showInfo}
        onCancel={() => setShowInfo(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfo(false)}>
            Got it
          </Button>,
        ]}
        width={560}
        destroyOnHidden
      >
        <p className="hms-modal-note">
          This page shows <strong>your assigned housekeeping tasks</strong>. Use the tabs and filters to narrow the list,
          and search to find a room or title.
        </p>
        <Section title="What you can do">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Edit a task to update its notes and status.</li>
            <li>Open the checklist to tick items off. The task completes automatically when every item is done.</li>
            <li>Turn on Auto refresh to reload the list every 60 seconds.</li>
          </ul>
        </Section>
      </Modal>
    </div>
  );
};

export default HousekeepingPage;
